import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { ExternalLink, Zap, User, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ZapButton } from '@/components/ZapButton';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import type { AppListing } from '@/lib/appTypes';
import { APP_LISTING_KIND } from '@/lib/appTypes';

interface AppCardProps {
  app: AppListing;
}

export function AppCard({ app }: AppCardProps) {
  const poster = useAuthor(app.author);
  const creator = useAuthor(app.creator || app.author);
  const posterMetadata = poster.data?.metadata;
  const creatorMetadata = creator.data?.metadata;
  const [imageError, setImageError] = useState(false);

  const displayName = app.isPostedByCreator
    ? (creatorMetadata?.name || creatorMetadata?.display_name || genUserName(app.creator || app.author))
    : (creatorMetadata?.name || creatorMetadata?.display_name || genUserName(app.creator || app.author));
  const avatar = app.isPostedByCreator
    ? creatorMetadata?.picture
    : creatorMetadata?.picture;

  // Create naddr for this app
  const naddr = nip19.naddrEncode({
    kind: APP_LISTING_KIND,
    pubkey: app.author,
    identifier: app.id,
  });

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Card className="group overflow-hidden bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/10 hover:border-purple-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 backdrop-blur-md hover:-translate-y-2 hover:scale-[1.02]">
      <div className="relative">
        {/* App Image/Screenshot */}
        {app.image && !imageError ? (
          <div className="relative w-full h-48 overflow-hidden">
            <img
              src={app.image}
              alt={app.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          </div>
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-purple-900/20 to-purple-500/10 flex items-center justify-center">
            <div className="text-6xl opacity-20">📱</div>
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 right-3 flex gap-2">
          <Badge className="bg-purple-500/20 text-purple-200/90 border-purple-500/30 backdrop-blur-sm">
            {app.category}
          </Badge>
          {!app.isPostedByCreator && (
            <Badge variant="destructive" className="bg-orange-600/80 hover:bg-orange-600/90 border-orange-500/50 backdrop-blur-sm flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              <span className="hidden sm:inline">Not Posted by Creator</span>
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-6 space-y-4">
        {/* Logo and Title */}
        <div className="flex items-start gap-4">
          {app.logo ? (
            <img
              src={app.logo}
              alt={`${app.name} logo`}
              className="w-14 h-14 rounded-xl object-cover shadow-lg ring-1 ring-purple-500/20"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-900/20 flex items-center justify-center ring-1 ring-purple-500/20">
              <span className="text-2xl">📱</span>
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <Link to={`/${naddr}`}>
              <h3 className="text-lg font-semibold mb-1 bg-gradient-to-r from-purple-200 via-blue-200 to-purple-200 bg-clip-text text-transparent hover:from-blue-200 hover:via-purple-200 hover:to-blue-200 transition-all truncate animate-gradient bg-[length:200%_auto]">
                {app.name}
              </h3>
            </Link>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-purple-200/70 line-clamp-3 leading-relaxed">
          {app.description}
        </p>

        {/* Creator/Poster Info and Actions */}
        <div className="space-y-2">
          {!app.isPostedByCreator && (
            <div className="text-xs text-orange-400/80 flex items-center gap-1 px-2 py-1 bg-orange-500/10 rounded border border-orange-500/20">
              <AlertTriangle className="h-3 w-3" />
              <span>Posted by someone other than creator</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-purple-500/10">
            <Link 
              to={`/npub${nip19.npubEncode(app.creator || app.author).slice(4)}`}
              className="flex items-center gap-2 hover:bg-purple-500/10 px-2 py-1.5 rounded-lg transition-colors group/author"
            >
              <Avatar className="h-6 w-6 ring-1 ring-purple-500/20">
                <AvatarImage src={avatar} alt={displayName} />
                <AvatarFallback className="bg-purple-900/30 text-purple-300 text-xs">
                  <User className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm text-purple-200/80 font-medium group-hover/author:text-purple-100">
                  {displayName}
                </span>
                {!app.isPostedByCreator && (
                  <span className="text-xs text-purple-300/50">Creator</span>
                )}
              </div>
            </Link>

          <div className="flex items-center gap-2">
            {/* Zap Button */}
            <ZapButton event={app.event} />

            {/* Open App Button */}
            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 text-purple-200 hover:text-purple-100 transition-all text-sm font-medium border border-purple-500/20 hover:border-purple-500/40"
            >
              <span>Open</span>
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
